import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '@/lib/api'
import { idempotencyKey } from '@/lib/utils'
import type { components } from '@/api/schema'

/**
 * Me area: a person's own space. Hooks for my memory, notifications, access,
 * profile, security, notification preferences and data rights.
 */

type S = components['schemas']
export type MyMemory = S['MyMemory']
export type MyMemoryDetail = S['MyMemoryDetail']
export type MyErasureCase = S['MyErasureCase']
export type Notification = S['Notification']
export type NotificationPreferences = S['NotificationPreferences']
export type MyProfile = S['MyProfile']
export type MySession = S['MySession']
export type SecurityActivity = S['SecurityActivity']
export type MyToken = S['MyToken']
export type MyExport = S['MyExport']
export type MyOrgImpact = S['MyOrgImpact']

/* ─────────────────────────── my memory ─────────────────────────── */

export interface MemoryFilters {
  q?: string
  status?: 'all' | 'current' | 'ended'
  source?: 'all' | 'explicit' | 'inferred'
}

export const myMemoriesQuery = (orgId: string, f: MemoryFilters) =>
  queryOptions({
    queryKey: ['me', 'memories', orgId, f.q ?? '', f.status ?? 'all', f.source ?? 'all'] as const,
    queryFn: () => unwrap(api.GET('/esm/v1/me/memories', { params: { query: { q: f.q || undefined, status: f.status ?? 'all', source: f.source ?? 'all', limit: 100 } } })),
    placeholderData: keepPreviousData,
  })

export const myMemoryStatsQuery = (orgId: string) =>
  queryOptions({ queryKey: ['me', 'memories', orgId, 'stats'] as const, queryFn: () => unwrap(api.GET('/esm/v1/me/memories/stats')) })

export const myMemoryQuery = (orgId: string, id: string) =>
  queryOptions({
    queryKey: ['me', 'memories', orgId, 'detail', id] as const,
    queryFn: () => unwrap(api.GET('/esm/v1/me/memories/{memory_id}', { params: { path: { memory_id: id } } })),
    retry: false,
  })

export interface MemoryInput {
  title: string
  body: string
  type?: 'user' | 'feedback' | 'project' | 'reference'
  valid_from?: string
  valid_to?: string
}

export function useAddMemory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, ...body }: MemoryInput & { key: string }) =>
      unwrap(api.POST('/esm/v1/me/memories', { body: { type: 'user', ...body }, headers: { 'Idempotency-Key': key } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'memories'] }),
  })
}

export function useCorrectMemory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: MemoryInput & { id: string; base_version?: number }) =>
      unwrap(api.POST('/esm/v1/me/memories/{memory_id}/versions', { params: { path: { memory_id: id } }, body, headers: { 'Idempotency-Key': idempotencyKey() } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'memories'] }),
  })
}

export function useEndMemory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, valid_to }: { id: string; valid_to?: string }) =>
      unwrap(api.POST('/esm/v1/me/memories/{memory_id}/end', { params: { path: { memory_id: id } }, body: { valid_to } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'memories'] }),
  })
}

export const requestMemoryErasure = (id: string) =>
  unwrap(api.POST('/esm/v1/me/memories/{memory_id}/erasure', { params: { path: { memory_id: id } } }))
export const requestAllErasure = () => unwrap(api.POST('/esm/v1/me/erasure'))
export const confirmErasure = (caseId: string) =>
  unwrap(api.POST('/esm/v1/me/erasure/{case_id}/confirm', { params: { path: { case_id: caseId } } }))

/* ─────────────────────────── notifications ─────────────────────────── */

export const notificationSummaryQuery = () =>
  queryOptions({
    queryKey: ['me', 'notifications', 'summary'] as const,
    queryFn: () => unwrap(api.GET('/esm/v1/me/notifications/summary')),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  })

export const notificationsQuery = (status: 'all' | 'unread' | 'archived', category?: string, cursor?: string) =>
  queryOptions({
    queryKey: ['me', 'notifications', 'list', status, category ?? '', cursor ?? ''] as const,
    queryFn: () => unwrap(api.GET('/esm/v1/me/notifications', { params: { query: { status, category: category || undefined, cursor, limit: 50 } } })),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  })

export function useNotificationActions() {
  const qc = useQueryClient()
  const done = () => qc.invalidateQueries({ queryKey: ['me', 'notifications'] })
  return {
    read: useMutation({
      mutationFn: ({ id, read }: { id: string; read: boolean }) => unwrap(api.POST('/esm/v1/me/notifications/{notification_id}/read', { params: { path: { notification_id: id } }, body: { read } })),
      onSuccess: done,
    }),
    archive: useMutation({
      mutationFn: ({ id, archived }: { id: string; archived: boolean }) => unwrap(api.POST('/esm/v1/me/notifications/{notification_id}/archive', { params: { path: { notification_id: id } }, body: { archived } })),
      onSuccess: done,
    }),
    readAll: useMutation({
      mutationFn: (category?: string) => unwrap(api.POST('/esm/v1/me/notifications/read-all', { body: { category } })),
      onSuccess: done,
    }),
    test: useMutation({
      mutationFn: () => unwrap(api.POST('/esm/v1/me/notifications/test')),
      onSuccess: done,
    }),
  }
}

export const notificationPrefsQuery = () =>
  queryOptions({ queryKey: ['me', 'notification-prefs'] as const, queryFn: () => unwrap(api.GET('/esm/v1/me/notification-preferences')) })

export function useSaveNotificationPrefs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { categories?: Record<string, { in_app: boolean; email: boolean }>; digest?: 'off' | 'daily' | 'weekly'; quiet_hours?: { enabled: boolean; start: string; end: string } }) =>
      unwrap(api.PUT('/esm/v1/me/notification-preferences', { body })),
    onSuccess: d => qc.setQueryData(notificationPrefsQuery().queryKey, d),
  })
}

/* ─────────────────────────── profile ─────────────────────────── */

export const profileQuery = () => queryOptions({ queryKey: ['me', 'profile'] as const, queryFn: () => unwrap(api.GET('/esm/v1/me/profile')) })

export function useSaveProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name?: string; preferred_name?: string | null; timezone?: string; locale?: 'en-IN' | 'en-GB' | 'en-US' | 'hi-IN' }) => unwrap(api.PATCH('/esm/v1/me/profile', { body })),
    onSuccess: d => {
      qc.setQueryData(profileQuery().queryKey, d)
      void qc.invalidateQueries({ queryKey: ['session'] })
    },
  })
}

export function useAvatar() {
  const qc = useQueryClient()
  const done = (d: MyProfile) => {
    qc.setQueryData(profileQuery().queryKey, d)
    void qc.invalidateQueries({ queryKey: ['session'] })
  }
  return {
    upload: useMutation({
      mutationFn: (body: { content_type: string; data_base64: string }) => unwrap(api.POST('/esm/v1/me/avatar', { body })),
      onSuccess: done,
    }),
    remove: useMutation({ mutationFn: () => unwrap(api.DELETE('/esm/v1/me/avatar')), onSuccess: done }),
  }
}

/* ─────────────────────────── security ─────────────────────────── */

export const securityQuery = () => queryOptions({ queryKey: ['me', 'security'] as const, queryFn: () => unwrap(api.GET('/esm/v1/me/security')) })
export const sessionsQuery = () => queryOptions({ queryKey: ['me', 'sessions'] as const, queryFn: () => unwrap(api.GET('/esm/v1/me/sessions')) })
export const activityQuery = () => queryOptions({ queryKey: ['me', 'activity'] as const, queryFn: () => unwrap(api.GET('/esm/v1/me/security/activity', { params: { query: { limit: 30 } } })) })

export function useSecurityActions() {
  const qc = useQueryClient()
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['me'] })
    void qc.invalidateQueries({ queryKey: ['session'] })
  }
  return {
    changePassword: useMutation({
      mutationFn: (body: { current_password: string; new_password: string; sign_out_other_sessions: boolean }) => unwrap(api.POST('/esm/v1/me/password', { body })),
      onSuccess: done,
    }),
    enrol: useMutation({ mutationFn: (password?: string) => unwrap(api.POST('/esm/v1/me/two-factor/enrol', { body: { password } })) }),
    verify: useMutation({ mutationFn: (code: string) => unwrap(api.POST('/esm/v1/me/two-factor/verify', { body: { code } })), onSuccess: done }),
    disable: (password?: string) => unwrap(api.POST('/esm/v1/me/two-factor/disable', { body: { password } })),
    backupCodes: (password?: string) => unwrap(api.POST('/esm/v1/me/two-factor/backup-codes', { body: { password } })),
    revokeSession: useMutation({
      mutationFn: (id: string) => unwrap(api.DELETE('/esm/v1/me/sessions/{session_id}', { params: { path: { session_id: id } } })),
      onSuccess: done,
    }),
    revokeOthers: useMutation({ mutationFn: () => unwrap(api.POST('/esm/v1/me/sessions/revoke-others')), onSuccess: done }),
    done,
  }
}

/* ─────────────────────────── access ─────────────────────────── */

export const accessQuery = () => queryOptions({ queryKey: ['me', 'access'] as const, queryFn: () => unwrap(api.GET('/esm/v1/me/access')) })
export const revokeToken = (id: string) => unwrap(api.POST('/esm/v1/me/tokens/{token_id}/revoke', { params: { path: { token_id: id } } }))

/* ─────────────────────────── data ─────────────────────────── */

export const dataQuery = () =>
  queryOptions({
    queryKey: ['me', 'data'] as const,
    queryFn: () => unwrap(api.GET('/esm/v1/me/data')),
    // Keep following an export while it is being built.
    refetchInterval: q => (q.state.data?.exports.some(e => e.status === 'queued' || e.status === 'running') ? 2000 : false),
  })

export function useRequestExport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => unwrap(api.POST('/esm/v1/me/exports', { headers: { 'Idempotency-Key': idempotencyKey() } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'data'] }),
  })
}

export const exportDownloadUrl = (id: string) => `/esm/v1/me/exports/${encodeURIComponent(id)}/download`
export const deleteAccount = (confirm: string) => unwrap(api.POST('/esm/v1/me/account/deletion', { body: { confirm } }))
